import { UserIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import Pagination from "../../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../../components/ui/dashboard/Table";
import { buildApiUrl, formatDate } from "../../../lib/utils";
import {
  FuelLog,
  FuelLogHistorySearchRequest,
  FuelLogSearchResponse,
} from "../@types";
import { useGetFuelLogHistoryMutation } from "../fuelLogApiSlice";
import ExportFuelLogModal from "./ExportFuelLogModal";

type FuelLogConsumptionHistoryProps = {
  vehicleId: string;
};

const FuelLogConsumptionHistory: React.FC<FuelLogConsumptionHistoryProps> = ({
  vehicleId,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // RTK Query mutation
  const [getFuelLog, { data: maintenanceData, isLoading }] =
    useGetFuelLogHistoryMutation();

  const fetchFuelLogs = useCallback(async () => {
    const requestBody: FuelLogHistorySearchRequest = {
      pageNumber: currentPage,
      pageSize: pageSize,
      vehicleId: vehicleId,
    };

    try {
      await getFuelLog(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch fuel logs:", err);
    }
  }, [currentPage, pageSize, getFuelLog]);

  // Fetch fuel logs on component mount and when filters change
  useEffect(() => {
    fetchFuelLogs();
  }, [currentPage, pageSize, fetchFuelLogs]);

  // Table columns configuration
  const columns: Column<FuelLog>[] = [
    {
      key: "createdOn",
      header: "Date",
      width: "20",
      render: (value: string) => (
        <span className="text-hColor">{formatDate(value)}</span>
      ),
    },
    {
      key: "vehicleModel",
      header: "Vehicle",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "driverName",
      header: "Driver",
      width: "",
      render: (value: string, item: FuelLog) => (
        <div className="flex items-center space-x-3">
          {item.driverImage ? (
            <img
              src={buildApiUrl(item.driverImage)}
              alt={`${item.driverName}`}
              className="w-[30px] h-[30px] rounded-full object-cover border border-gray-300 shadow-sm"
            />
          ) : (
            <div className="w-[30px] h-[30px] rounded-full bg-gray-200 flex items-center justify-center border border-gray-300 shadow-sm p-1">
              <UserIcon className="w-full h-full text-hColor" />
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-hColor capitalize">{item.driverName}</span>
          </div>
        </div>
      ),
    },
    {
      key: "fuelType",
      header: "FuelLog type",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "fuelCost",
      header: "Fuel cost (NGN)",
      width: "16",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "fuelPurchased",
      header: "Fuel consumed",
      width: "16",
      render: (value: string) => (
        <span className="text-hColor">{value} Litters</span>
      ),
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const response = maintenanceData?.data as FuelLogSearchResponse | undefined;

  return (
    <div className="bg-white rounded-[10px] p-4">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h2 className="text-md font-semibold text-hColor">
          Consumption history
        </h2>
        <div className="">
          <ExportFuelLogModal vehicleId={vehicleId} />
        </div>
      </div>
      {/* Table */}
      <div className="overflow-hidden space-y-6">
        <Table
          data={response?.data || []}
          columns={columns}
          loading={isLoading}
          emptyMessage="No fuel logs found"
        />

        {/* Pagination */}
        {response && (
          <Pagination
            currentPage={response.currentPage}
            totalPages={response.totalPages}
            pageSize={response.pageSize}
            totalCount={response.totalCount}
            hasNextPage={response.hasNextPage}
            hasPreviousPage={response.hasPreviousPage}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>
    </div>
  );
};

export default FuelLogConsumptionHistory;
